import { API_URL } from "../../../../constants/urls.constants";
import type {
  CommonResponse,
  FailedJobResponse,
  FailedJobsParams,
  GetQueryArgsType,
} from "../../../../types/global.types";
import { appServiceApi } from "../../../apis/app-service.api";
import { APP_API_TAGS } from "../../../app.constants";
import type {
  AllHolidaysResponse,
  AllLeaveResponse,
  FailedHolidayListParams,
  FailedHolidayListResponse,
  FailedLeaveListParams,
  FailedLeaveListResponse,
  GetAllHolidaysParams,
  GetLeaveApplicationsParams,
  GetMonthlyHolidaysParams,
  GetMonthlyLeavesParams,
  GetMonthlyLeavesResponse,
  MonthlyHolidaysResponse,
} from "./emp-leave.type";

const { LEAVES, HOLIDAYS } = APP_API_TAGS || {};

export const empLeaveApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Start: API Endpoints for Leave Management ------
    approveLeaveApplication: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.leaveApplications}/${id}/approve`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [LEAVES],
    }),

    rejectLeaveApplication: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.leaveApplications}/${id}/reject`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [LEAVES],
    }),

    getAllLeaveApplications: builder.query<
      AllLeaveResponse,
      GetQueryArgsType<GetLeaveApplicationsParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.leaveApplications,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [LEAVES],
    }),
    // ------ END: API Endpoints for Leave Management ------

    // ------ Start: API Endpoints for Holyday Management ------
    createHoliday: builder.mutation({
      query: (data) => ({
        url: API_URL.holidays,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (res) => (res ? [HOLIDAYS] : []),
    }),
    updateHoliday: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.holidays}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (res) => (res ? [HOLIDAYS] : []),
    }),
    deleteHoliday: builder.mutation({
      query: (id) => ({
        url: `${API_URL.holidays}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (res) => (res ? [HOLIDAYS] : []),
    }),

    createBulkHolidays: builder.mutation({
      query: (data) => ({
        url: `${API_URL.holidays}/bulk`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [HOLIDAYS],
    }),

    getMonthlyLeaves: builder.query<
      GetMonthlyLeavesResponse,
      GetQueryArgsType<GetMonthlyLeavesParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.leaveApplications}/monthly`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [LEAVES],
    }),

    getAllHolidays: builder.query<
      AllHolidaysResponse,
      GetQueryArgsType<GetAllHolidaysParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.holidays,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [HOLIDAYS],
    }),

    getMonthlyHolidays: builder.query<
      MonthlyHolidaysResponse,
      GetQueryArgsType<GetMonthlyHolidaysParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.holidays}/monthly`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [HOLIDAYS],
    }),
    getFailedHolidayJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.holidays}/failed-holidays/jobs`,
        ...queryArgs,
      }),
      providesTags: [HOLIDAYS],
    }),
    getFailedHolidayList: builder.query<
      CommonResponse<FailedHolidayListResponse>,
      GetQueryArgsType<FailedHolidayListParams>
    >({
      query: (queryArgs) => ({
        url: `/holidays/failed-holidays/list`, // Updated to match user request
        ...queryArgs,
      }),
      providesTags: [HOLIDAYS],
    }),
    downloadHolidayTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.holidays}/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadHoliday: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.holidays}/upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [HOLIDAYS] : []),
    }),
    downloadLeaveTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.leaveApplications}/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadLeave: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.leaveApplications}/upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [LEAVES] : []),
    }),
    getFailedLeaveJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.leaveApplications}/failed-leaves/jobs`,
        ...queryArgs,
      }),
      providesTags: [LEAVES],
    }),
    getFailedLeaveRecords: builder.query<
      CommonResponse<FailedLeaveListResponse>,
      GetQueryArgsType<FailedLeaveListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.leaveApplications}/failed-leaves/list`,
        ...queryArgs,
      }),
      providesTags: [LEAVES],
    }),
    // ------ Start: END Endpoints for Leave Management ------
  }),
});

export const {
  // Leave Management
  useApproveLeaveApplicationMutation,
  useRejectLeaveApplicationMutation,
  useGetAllLeaveApplicationsQuery,
  useGetMonthlyLeavesQuery,

  // Holiday Management
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useCreateBulkHolidaysMutation,
  useGetAllHolidaysQuery,
  useGetMonthlyHolidaysQuery,
  useGetFailedHolidayJobsQuery,
  useGetFailedHolidayListQuery,
  useLazyDownloadHolidayTemplateQuery,
  useUploadHolidayMutation,

  // Leave Bulk Upload
  useLazyDownloadLeaveTemplateQuery,
  useUploadLeaveMutation,
  useGetFailedLeaveJobsQuery,
  useGetFailedLeaveRecordsQuery,
} = empLeaveApi;
